import type { EngineInput, EngineResult, EstimateUnit, PricingMode, ProfitLeak } from '../estimate.types';
import { resolveEstimateRate, type EstimateRateType } from '../estimate.rates';
import { createEstimateItem } from '../estimateItemFactory';
import { suggestMaterials } from '../materialSuggestions';
import { multiplyMoney, sumMoney } from '../money';

function formatNumber(value: number): string {
  const rounded = Math.round(value * 100) / 100;
  return Number.isInteger(rounded) ? rounded.toFixed(0) : String(rounded);
}

function createBathroomItem(input: {
  id: string;
  label: string;
  quantity: number;
  unit: string;
  rateType: EstimateRateType;
  category: string;
  pricingMode: PricingMode;
  order?: number;
  region?: string;
  userRates?: EngineInput['userRates'];
}) {
  const unitRateNetto = resolveEstimateRate(input.rateType, input.region, input.userRates);

  return createEstimateItem({
    id: input.id,
    label: input.label,
    category: input.category,
    quantity: input.quantity,
    unit: input.unit as EstimateUnit,
    unitPrice: unitRateNetto,
    pricingMode: input.pricingMode,
    order: input.order,
  });
}

function emptyMeta(input: EngineInput, totalWorkingArea: number): EngineResult['meta'] {
  const floorArea = input.floorArea ?? 0;

  return {
    perimeter: input.perimeter ?? 0,
    length: input.length ?? 0,
    width: input.width ?? 0,
    height: input.height ?? 0,
    floorArea,
    openingsArea: input.openingsArea ?? input.bigWindowsArea ?? 0,
    wallArea: 0,
    wallsGross: 0,
    doorsDeduction: 0,
    bigWindowsDeduction: 0,
    ceilingArea: floorArea,
    effectiveArea: 0,
    wallsNet: 0,
    totalWorkingArea,
    calculations: {
      floorArea: floorArea > 0 ? `powierzchnia łazienki = ${formatNumber(floorArea)} m²` : 'brak obmiaru podłogi',
      perimeter: 'łazienka: obmiar branżowy',
      wallsGross: 'łazienka: pozycje liczone z pól branżowych',
      doorsDeduction: '0 m²',
      bigWindowsDeduction: '0 m²',
      wallsNet: 'łazienka: nie dotyczy',
      totalWorkingArea: `${formatNumber(totalWorkingArea)} m²`,
      measurementMode: 'bathroom_basic',
      calculationNote: 'MVP łazienki: pozycje liczone z pól branżowych. AI i reguły tylko sugerują ryzyka.',
    },
  };
}

export function calculateBathroomEstimate(input: EngineInput): EngineResult {
  const tilesArea = input.tilesAreaM2 ?? 0;
  const hydroArea = input.hydroAreaM2 ?? 0;
  const waterPoints = input.waterPoints ?? 0;
  const electricPoints = input.electricPoints ?? 0;
  const demolitionArea = input.tileRemovalAreaM2 ?? 0;
  const demolitionItems = input.demolitionItems ?? 0;
  const grooveLength = input.grooveLengthM ?? 0;
  const items = [
    ...(hydroArea > 0
      ? [
          createBathroomItem({
            id: 'bath_hydro',
            label: 'Hydroizolacja',
            quantity: hydroArea,
            unit: 'm²',
            rateType: 'bath_hydro',
            category: 'hydro',
            pricingMode: 'area',
            order: 10,
            region: input.region,
            userRates: input.userRates,
          }),
        ]
      : []),
    ...(tilesArea > 0
      ? [
          createBathroomItem({
            id: 'bath_tiles',
            label: 'Układanie płytek / gresu',
            quantity: tilesArea,
            unit: 'm²',
            rateType: 'bath_tiles',
            category: 'plytki',
            pricingMode: 'area',
            order: 20,
            region: input.region,
            userRates: input.userRates,
          }),
        ]
      : []),
    ...(waterPoints > 0
      ? [
          createBathroomItem({
            id: 'bath_water_point',
            label: 'Punkt wod-kan',
            quantity: waterPoints,
            unit: 'szt',
            rateType: 'bath_water_point',
            category: 'wodkan',
            pricingMode: 'piece',
            order: 30,
            region: input.region,
            userRates: input.userRates,
          }),
        ]
      : []),
    ...(electricPoints > 0
      ? [
          createBathroomItem({
            id: 'bath_electric_point',
            label: 'Punkt elektryczny',
            quantity: electricPoints,
            unit: 'szt',
            rateType: 'bath_electric_point',
            category: 'elektryka',
            pricingMode: 'piece',
            order: 40,
            region: input.region,
            userRates: input.userRates,
          }),
        ]
      : []),
    ...(demolitionArea > 0
      ? [
          createBathroomItem({
            id: 'bath_tile_removal',
            label: 'Skuwanie płytek',
            quantity: demolitionArea,
            unit: 'm²',
            rateType: 'bath_tile_removal',
            category: 'demolition',
            pricingMode: 'area',
            order: 5,
            region: input.region,
            userRates: input.userRates,
          }),
        ]
      : []),
    ...(demolitionItems > 0
      ? [
          createBathroomItem({
            id: 'bath_demolition_items',
            label: 'Demontaże sanitarne',
            quantity: demolitionItems,
            unit: 'szt',
            rateType: 'bath_demolition',
            category: 'demontaz',
            pricingMode: 'piece',
            order: 6,
            region: input.region,
            userRates: input.userRates,
          }),
        ]
      : []),
    ...(grooveLength > 0
      ? [
          createBathroomItem({
            id: 'bath_groove_chasing',
            label: 'Bruzdy pod instalacje',
            quantity: grooveLength,
            unit: 'mb',
            rateType: 'bath_groove_chasing',
            category: 'bruzdy',
            pricingMode: 'linear',
            order: 15,
            region: input.region,
            userRates: input.userRates,
          }),
        ]
      : []),
  ];
  const cost = sumMoney(items.map((item) => item.totalNetto));
  const leaks: ProfitLeak[] = [];

  if (tilesArea > 0 && hydroArea <= 0) {
    const unitRate = resolveEstimateRate('bath_hydro', input.region, input.userRates);
    const value = multiplyMoney(tilesArea, unitRate);
    const calculation = `${formatNumber(tilesArea)} m² × ${formatNumber(unitRate)} zł/m² = ${formatNumber(value)} zł`;

    leaks.push({
      id: 'bath_missing_hydro',
      type: 'hydro',
      severity: 'critical',
      label: 'Brak hydroizolacji',
      message: 'Kładziesz płytki bez folii w płynie? Jeśli puści wilgoć, poprawki wrócą do Ciebie.',
      clientPdfMessage: 'Hydroizolacja zwiększa trwałość prac w strefie mokrej i ogranicza ryzyko reklamacji.',
      value,
      quantity: tilesArea,
      unit: 'm²',
      unitRate,
      calculation,
      aiAdded: false,
      dismissed: false,
      decisionStatus: 'recommended',
      suggestedItem: {
        id: 'bath_hydro',
        label: 'Hydroizolacja',
        quantity: tilesArea,
        unit: 'm²',
        unitPrice: unitRate,
        total: value,
        pricingMode: 'area',
        unitRateNetto: unitRate,
        totalNetto: value,
        calculation,
        category: 'hydro',
      },
    });
  }

  const result: EngineResult = {
    items,
    leaks,
    cost,
    meta: emptyMeta(input, Math.max(tilesArea, hydroArea, input.floorArea ?? 0)),
  };

  return {
    ...result,
    meta: {
      ...result.meta,
      materialSuggestions: suggestMaterials(input, result),
    },
  };
}
